import { getGeminiTokenUsage } from "./ai-usageFunctions";
import { createWebsiteInfoModel, editPageModel, fullPageModel, updateWebsiteMetaModel, userIntentModel } from "./models";
import { createWebsiteInfoPrompt, editFullPageHtmlCssPrompt, generateFullPageHtmlCssPrompt, getUserIntentPrompt, updateWebsiteMetaPrompt, getComponentsToEditFromAIPrompt } from "./prompts";

function extractJsonFromModelText(text) {
  // 1) Remove markdown fences
  const stripped = text
    .replace(/```json\s*/gi, "")
    .replace(/```\s*/g, "")
    .trim();

  // 2) Try direct parse
  try {
    return JSON.parse(stripped);
  } catch (e) {
    // 3) Try to locate first JSON object in the string
    const first = stripped.indexOf("{");
    const last = stripped.lastIndexOf("}");
    if (first !== -1 && last !== -1 && last > first) {
      const candidate = stripped.slice(first, last + 1);
      return JSON.parse(candidate);
    }
    throw e;
  }
}

export async function generateFullPageHtmlCssFromAI({ websiteName, websiteDescription, userRequest, userId, websiteId, action = "Generating a full Page." }) {
  const prompt = generateFullPageHtmlCssPrompt({ websiteName, websiteDescription, userRequest });
  const result = await fullPageModel.generateContent(prompt);
  await getGeminiTokenUsage({result, model: "gemini-2.5-flash", userId, websiteId, userMessage: userRequest, action});
  const text = result.response.text();
  return extractJsonFromModelText(text);
}

export async function editFullPageHtmlCssFromAI({ userRequest, currentHtml, currentCss, userId, websiteId, action = "Editing the page." }) {
  const prompt = editFullPageHtmlCssPrompt({ userRequest, currentHtml, currentCss });
  const result = await editPageModel.generateContent(prompt);
  await getGeminiTokenUsage({result, model: "gemini-2.5-flash", userId, websiteId, userMessage: userRequest, action});
  const text = result.response.text();
  return extractJsonFromModelText(text);
}

export async function getUserIntent({ userMessage, chattingName, userId, websiteId }) {
  const cleanMsg = (userMessage || "").toString().trim();
  const userName = (chattingName || "There").toString().trim();

  const prompt = getUserIntentPrompt({ userMessage: cleanMsg, chattingName: userName });

  console.log("Intent inputs:", { cleanMsg, userName });

  const result = await userIntentModel.generateContent(prompt);
  await getGeminiTokenUsage({result, model: "gemini-2.5-flash-lite", userId, websiteId, userMessage, action: "Checking user intetion."});
  const text = result.response.text();

  // If you're using responseMimeType: "application/json", this should already be JSON,
  // but keep this safe parse to avoid crashing on stray text.
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    console.error("getUserIntent JSON parse failed. Raw text:", text);
    return { userIntent: "Kairos can't do that yet", friendlyResponse: "I couldn't understand that." };
  }

  const est = Number(parsed.estimatedTokens);

  return {
    userIntent: (parsed.userIntent || "").trim(),
    estimatedTokens: Number.isFinite(est) && est >= 0 ? est : 0,
  };
}

export async function updateWebsiteMeta({ currentWebsiteName, currentWebsiteDescription, userMessage, chattingName, userId, websiteId, action }) {
  const cleanMsg = (userMessage || "").toString().trim();
  const userName = (chattingName || "There").toString().trim();

  const prompt = updateWebsiteMetaPrompt({ currentWebsiteName, currentWebsiteDescription, userRequest: cleanMsg, chattingName: userName });
  const result = await updateWebsiteMetaModel.generateContent(prompt);
  await getGeminiTokenUsage({ result, model: "gemini-2.5-flash-lite", userId, websiteId, userMessage: cleanMsg, action });
  const text = result.response.text();
  console.log(text);

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    console.error("updateWebsiteMeta JSON parse failed. Raw text:", text);
    return {
      newWebsiteName: "",
      newWebsiteDescription: "",
      friendlyResponse: "I couldn't understand that.",
    };
  }

  return {
    newWebsiteName: (parsed.newWebsiteName || "").trim(),
    newWebsiteDescription: (parsed.newWebsiteDescription || "").trim(),
    friendlyResponse: (parsed.friendlyResponse || "").trim(),
  };
}


export async function createWebsiteInfo({ userRequest, chattingName = "" }) {
  const safeName = (chattingName || "There").trim();

  const prompt = createWebsiteInfoPrompt({ userRequest, chattingName: safeName });

  const result = await createWebsiteInfoModel.generateContent(prompt);

  const text = result.response.text();
  const parsed = JSON.parse(text);

  return {
    websiteName: (parsed.websiteName || "").trim(),
    websiteDescription: (parsed.websiteDescription || "").trim(),
    friendlyResponse: (parsed.friendlyResponse || "").trim(),
    userAction: (parsed.userAction || "").trim(),
    kairosAction: (parsed.kairosAction || "").trim(),
    result,
  };
}



export async function getComponentsToEditFromAI({ userMessage, pageSummary, selectorList, userId, websiteId, html }) {
  const prompt = getComponentsToEditFromAIPrompt({
    userMessage,
    pageSummary,
    selectorList,
    html,
  });

  const result = await userIntentModel.generateContent(prompt);

  await getGeminiTokenUsage({ result, model: "gemini-2.5-flash-lite", userId, websiteId, userMessage: userMessage, action: "Checking type of edit." });

  const text = result.response.text();

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    console.error("getEditPlanFromAI JSON parse failed. Raw text:", text);
    return {
      editType: "Unknown",
      targetSelectors: [],
      position: "",
    };
  }

  return {
    editType: (parsed.editType || "Unknown").trim(),
    targetSelectors: Array.isArray(parsed.targetSelectors) ? parsed.targetSelectors : [],
    position: (parsed.position || "").trim(),
  };
}